/**
 * Managing DMs and group DMs (user-management §3.3, Discord's rules): the
 * friend picker (a new DM or group, or friends added to a group), Rename,
 * the members list (owner: Make group owner, Remove), Leave group, and
 * Close DM. The DM list's row menu and a group's header both use these.
 */
import {
  ChannelType,
  GROUP_DM_MAX_RECIPIENTS,
  normalizeForSearch,
  RelationshipType,
  type Channel,
  type Relationship,
  type Snowflake,
  type User,
} from '@neosocial/protocol';
import { useState } from 'react';
import { create } from 'zustand';
import { useT } from '../i18n';
import { tr } from '../i18n';
import { useData, useDataShallow } from '../store/data';
import { displayName, privateChannelName } from '../store/selectors';
import { Avatar, Btn, Icon, IconBtn } from '../ui/kit';
import { Dialog, openMenu, openPopover, type MenuItem } from '../ui/overlay';
import { openProfile } from './ProfileCard';
import {
  addToGroup,
  closeDm,
  createGroupDm,
  leaveGroup,
  makeGroupOwner,
  openDm,
  removeFromGroup,
  renameGroup,
} from './social';

/** The friends search: Arabic-script letters and digits normalised, a leading "@" ignored. */
export function matchesPerson(user: User, query: string): boolean {
  const q = normalizeForSearch(query.trim().replace(/^@/, ''));
  if (!q) return true;
  return (
    normalizeForSearch(displayName(user)).includes(q) ||
    normalizeForSearch(user.username).includes(q)
  );
}

type DmDialog =
  | { kind: 'new' }
  | { kind: 'add'; channelId: Snowflake }
  | { kind: 'rename'; channelId: Snowflake };

const useDmDialog = create<{ open: DmDialog | null }>(() => ({ open: null }));

export function openDmDialog(d: DmDialog): void {
  useDmDialog.setState({ open: d });
}

/** Where the DM dialogs open (Home's DM list is on screen whenever a DM is). */
export function DmDialogs() {
  const open = useDmDialog((s) => s.open);
  const close = () => useDmDialog.setState({ open: null });
  if (!open) return null;
  if (open.kind === 'rename') return <RenameDialog channelId={open.channelId} onClose={close} />;
  return (
    <PickFriendsDialog channelId={open.kind === 'add' ? open.channelId : null} onClose={close} />
  );
}

/** A new DM or group from your friends, or (with `channelId`) friends added to a group. */
function PickFriendsDialog({
  channelId,
  onClose,
}: {
  channelId: Snowflake | null;
  onClose(): void;
}) {
  const { t } = useT();
  const group = useData((d) => (channelId ? d.channels[channelId] : undefined));
  const inGroup = new Set((group?.recipients ?? []).map((u) => u.id));
  const friends = useDataShallow((d) =>
    Object.values(d.relationships).filter((r) => r.type === RelationshipType.FRIEND),
  ).filter((r: Relationship) => !inGroup.has(r.id));
  const [picked, setPicked] = useState<Snowflake[]>([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const shown = friends.filter((r) => matchesPerson(r.user, q));
  // you count too: a group holds 10 people, you included
  const max = GROUP_DM_MAX_RECIPIENTS - 1 - (group ? inGroup.size : 0);
  const adding = !!channelId;
  return (
    <Dialog
      title={adding ? t('social.gdm.add') : t('hubv.dm.create')}
      icon={adding ? 'user-plus' : 'users'}
      tone="mode"
      cls="ns-gdm-dlg"
      onClose={onClose}
      actions={
        <>
          <Btn size="sm" onClick={onClose}>
            {t('c.cancel')}
          </Btn>
          <Btn
            kind="primary"
            size="sm"
            disabled={!picked.length || busy}
            busy={busy}
            onClick={async () => {
              if (adding) {
                setBusy(true);
                const ok = await addToGroup(channelId, picked);
                setBusy(false);
                if (ok) onClose();
                return;
              }
              onClose();
              if (picked.length === 1) void openDm(picked[0]!);
              else void createGroupDm(picked);
            }}
          >
            {adding
              ? t('social.gdm.add')
              : picked.length > 1
                ? t('ns.dm.createGroup')
                : t('hub.prof.message')}
          </Btn>
        </>
      }
    >
      <p>{max > 0 ? t('ns.dm.pick', { n: max }) : t('gdm.full')}</p>
      <input
        className="nx-input"
        dir="auto"
        placeholder={t('hubv.fr.search')}
        aria-label={t('hubv.fr.search')}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        data-autofocus
      />
      <ul className="ns-pick nx-scroll">
        {shown.map((r) => {
          const on = picked.includes(r.id);
          return (
            <li key={r.id}>
              <label>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!on && picked.length >= max}
                  onChange={() =>
                    setPicked((l) => (on ? l.filter((x) => x !== r.id) : [...l, r.id]))
                  }
                />
                <Avatar user={r.user} size={28} />
                <span dir="auto">{displayName(r.user)}</span>
              </label>
            </li>
          );
        })}
        {!shown.length ? (
          <li className="ns-muted">
            {q.trim() && friends.length
              ? t('hubv.fr.none', { q: q.trim() })
              : adding
                ? t('social.gdm.noneToAdd')
                : t('ns.dm.noFriends')}
          </li>
        ) : null}
      </ul>
    </Dialog>
  );
}

function RenameDialog({ channelId, onClose }: { channelId: Snowflake; onClose(): void }) {
  const { t } = useT();
  const current = useData((d) => d.channels[channelId]?.name ?? '');
  const [v, setV] = useState(current);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (v.trim() === current) return onClose();
    setBusy(true);
    const ok = await renameGroup(channelId, v);
    setBusy(false);
    if (ok) onClose();
  };
  return (
    <Dialog
      title={t('social.gdm.rename')}
      icon="edit"
      tone="mode"
      onClose={onClose}
      actions={
        <>
          <Btn size="sm" onClick={onClose}>
            {t('c.cancel')}
          </Btn>
          <Btn kind="primary" size="sm" busy={busy} disabled={busy} onClick={() => void save()}>
            {t('c.save')}
          </Btn>
        </>
      }
    >
      <form
        className="ns-field"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <label>
          <span className="ns-label">{t('gdm.name')}</span>
          <input
            className="nx-input"
            dir="auto"
            maxLength={100}
            value={v}
            autoComplete="off"
            data-autofocus
            onChange={(e) => setV(e.target.value)}
          />
        </label>
        <p className="ns-muted">{t('social.gdm.nameHint')}</p>
      </form>
    </Dialog>
  );
}

/** The menu of a DM list row (right-click, or the row's close button's neighbour). */
export function dmMenuItems(c: Channel): MenuItem[] {
  const { t } = tr();
  const d = useData.getState();
  const name = privateChannelName(d, c);
  if (c.type === ChannelType.GROUP_DM) {
    return [
      {
        label: t('social.gdm.add'),
        icon: 'user-plus',
        run: () => openDmDialog({ kind: 'add', channelId: c.id }),
      },
      {
        label: t('social.gdm.rename'),
        icon: 'edit',
        run: () => openDmDialog({ kind: 'rename', channelId: c.id }),
      },
      { sep: true },
      {
        label: t('gdm.leave'),
        icon: 'arrow-out',
        danger: true,
        run: () => void leaveGroup(c.id, name),
      },
    ];
  }
  const peer = c.recipients?.find((u) => u.id !== d.me?.id);
  return [
    ...(peer
      ? [{ label: t('pres.profile'), icon: 'user' as const, run: () => openProfile(peer.id, null) }]
      : []),
    { sep: true },
    { label: t('dm.close'), icon: 'x', run: () => void closeDm(c.id) },
  ];
}

function GroupMembers({ channelId, close }: { channelId: Snowflake; close(): void }) {
  const { t } = useT();
  const c = useData((d) => d.channels[channelId]);
  const me = useData((d) => d.me);
  if (!c || !me) return null;
  const amOwner = c.owner_id === me.id;
  const people = [me, ...(c.recipients ?? []).filter((u) => u.id !== me.id)];
  return (
    <div className="ns-gdm-mem">
      <div className="ns-pop-h">
        <Icon name="users" />
        <b>{t('social.gdm.members')}</b>
        <span className="ns-muted">{t('hubv.dm.members', { n: people.length })}</span>
      </div>
      <ul>
        {people.map((u) => {
          const name = displayName(u);
          const owner = c.owner_id === u.id;
          const items: MenuItem[] = [
            { label: t('pres.profile'), icon: 'user', run: () => openProfile(u.id, null) },
          ];
          if (amOwner && u.id !== me.id) {
            items.push(
              {
                label: t('gdm.transfer'),
                icon: 'crown',
                run: () => void makeGroupOwner(c.id, u.id, name),
              },
              { sep: true },
              {
                label: t('social.gdm.remove'),
                icon: 'x',
                danger: true,
                run: () => void removeFromGroup(c.id, u.id, name),
              },
            );
          }
          return (
            <li key={u.id}>
              <button
                type="button"
                className="ns-gdm-u"
                onClick={() => {
                  close();
                  openProfile(u.id, null);
                }}
              >
                <Avatar user={u} size={28} />
                <span dir="auto">{name}</span>
                {owner ? (
                  <span
                    className="mem-ic crown"
                    data-tip={t('gdm.owner')}
                    aria-label={t('gdm.owner')}
                  >
                    <Icon name="crown" />
                  </span>
                ) : null}
              </button>
              {items.length > 1 ? (
                <IconBtn
                  icon="more"
                  label={t('hub.prof.more')}
                  onClick={(e) => openMenu(e.currentTarget, items, { placement: 'bottom-end' })}
                />
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * A group DM's header: members, Add friends, and Rename / Leave under More.
 * `compact` (a phone, where the header holds at most three actions after
 * Pins; §12.7 A1): Add friends moves into More.
 */
export function GroupHeaderActions({ channel, compact }: { channel: Channel; compact?: boolean }) {
  const { t } = useT();
  return (
    <>
      {!compact ? (
        <IconBtn
          icon="user-plus"
          label={t('social.gdm.add')}
          tipPos="bottom"
          onClick={() => openDmDialog({ kind: 'add', channelId: channel.id })}
        />
      ) : null}
      <IconBtn
        icon="users"
        label={t('social.gdm.members')}
        tipPos="bottom"
        onClick={(e) =>
          openPopover(
            e.currentTarget,
            (close) => <GroupMembers channelId={channel.id} close={close} />,
            {
              placement: 'bottom-end',
              cls: 'ns-gdm-pop',
              width: 300,
            },
          )
        }
      />
      <IconBtn
        icon="more"
        label={t('hub.prof.more')}
        tipPos="bottom"
        aria-haspopup="menu"
        onClick={(e) =>
          openMenu(
            e.currentTarget,
            compact
              ? dmMenuItems(channel)
              : dmMenuItems(channel).filter((i) => i.icon !== 'user-plus'),
            {
              placement: 'bottom-end',
            },
          )
        }
      />
    </>
  );
}
